import { createContext, FC, useCallback, useMemo, useState } from 'react';
import { RESTRICTION_ACCESS } from 'src/constants/integrations';
import { defaulLangTextValidation, LOCALES } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { ILangTextValidation, TTranslate } from 'src/types/main';
import { getTranslate, randomFloatNumber, scrollToErrorElement } from 'src/utils/helpers';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import ModalContent from 'src/view/components/Controls/Modal/ModalContent/ModalContent';
import ModalFooter from 'src/view/components/Controls/Modal/ModalFooter/ModalFooter';
import ModalHeader from 'src/view/components/Controls/Modal/ModalHeader/ModalHeader';
import SectionName from 'src/view/containers/Modals/DineInSectionModal/components/SectionName/SectionName';
import TablesList from 'src/view/containers/Modals/DineInSectionModal/components/TablesList/TablesList';
import { IEditableBranchSectionTable } from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/types/branchEdit';

interface IRestrictionAccessContext {
  restrictionAccess: RESTRICTION_ACCESS | undefined;
  isDisabledForEditing: boolean;
}

/**
 * Context used to handle table management accessibility
 */
export const RestrictionAccessContext = createContext<IRestrictionAccessContext>({
  restrictionAccess: RESTRICTION_ACCESS.ALLOWED,
  isDisabledForEditing: false,
});

const DineInSectionModal: FC = (): JSX.Element => {
  const modalParams = useMemo(
    () => ({
      sectionId: modalStore.dineInSectionModal!.id,
      sectionName: modalStore.dineInSectionModal!.name,
      sectionTables: modalStore.dineInSectionModal!.tables,
      restrictionAccess: modalStore.dineInSectionModal!.restrictionAccess,
      isDisabledForEditing: modalStore.dineInSectionModal!.disabledForEditing,
      onSave: modalStore.dineInSectionModal!.onSave,
    }),
    []
  );

  const [sectionName, setSectionName] = useState<TTranslate>(modalParams.sectionName || { ar: '', en: '' });
  const [sectionTables, setSectionTables] = useState<IEditableBranchSectionTable[]>(() => {
    return modalParams.sectionTables?.length ? modalParams.sectionTables : [getEmptyTableObj()];
  });

  const [sectionNameValidation, setSectionNameValidation] = useState<ILangTextValidation>({ ...defaulLangTextValidation });
  const [sectionTablesValidation, setSectionTableValidation] = useState<{ id: number; nameValidation: ILangTextValidation }[]>([]);

  const onChangeSectionName = useCallback((value: string, locale: LOCALES) => {
    setSectionName((currSectionName) => ({ ...currSectionName, [locale]: value }));
  }, []);

  const onAddTable = useCallback(() => {
    setSectionTables((currSectionTables) => [...currSectionTables, getEmptyTableObj()]);
  }, []);

  const onDeleteTable = useCallback((id: number) => {
    setSectionTables((currSectionTables) => currSectionTables.filter((table) => table.id !== id));
  }, []);

  const onChangeTableName = useCallback((tableId: number, value: string, locale: LOCALES) => {
    setSectionTables((currSectionTables) =>
      currSectionTables.map((table) => {
        if (table.id === tableId) return { ...table, name: { ...table.name, [locale]: value } };
        return table;
      })
    );
  }, []);

  const onSaveChanges = () => {
    const isValidData = setValidationAndGetResult();

    if (isValidData) {
      modalParams.onSave(modalParams.sectionId, sectionName, sectionTables);
      modalStore.closeDineInSectionModal();
    } else {
      scrollToErrorElement();
    }
  };

  const setValidationAndGetResult = () => {
    // section name

    const [nameValidation, isValidName] = validateName(sectionName);

    setSectionNameValidation(nameValidation);

    // section tables

    const invalidTables: { id: number; nameValidation: ILangTextValidation }[] = [];

    // find all invalid tables
    sectionTables.forEach((table) => {
      const [tableNameValidation, isValidTable] = validateName(table.name);

      if (!isValidTable) {
        invalidTables.push({ id: table.id, nameValidation: tableNameValidation });
      }
    });

    setSectionTableValidation(invalidTables);

    return isValidName && invalidTables.length === 0;
  };

  return (
    <CustomModalComponent isOpen withCloseIcon modalMaxWidth={484} onRequestClose={modalStore.closeDineInSectionModal}>
      <ModalBackground>
        <ModalHeader title={modalParams.sectionName ? getTranslate(modalParams.sectionName) : localeStore.t('create_new_section_header')} />

        <ModalContent scrollSize="small" className="d-flex flex-1" scrollClassName="full-height">
          <RestrictionAccessContext.Provider
            value={{ restrictionAccess: modalParams.restrictionAccess, isDisabledForEditing: modalParams.isDisabledForEditing }}>
            <SectionName name={sectionName} validation={sectionNameValidation} onChangeName={onChangeSectionName} />
            <TablesList
              tables={sectionTables}
              namesValidation={sectionTablesValidation}
              onAddTable={onAddTable}
              onDeleteTable={onDeleteTable}
              onChangeTableName={onChangeTableName}
            />
          </RestrictionAccessContext.Provider>
        </ModalContent>

        <ModalFooter>
          <Button title={localeStore.t('Save')} onClick={onSaveChanges} size="large" />
        </ModalFooter>
      </ModalBackground>
    </CustomModalComponent>
  );
};

const validateName = (name: TTranslate): [ILangTextValidation, boolean] => {
  const validation: ILangTextValidation = { ...defaulLangTextValidation };
  let isValid = true;

  if (!name.ar?.trim()) {
    [validation.arValid, isValid] = [false, false];
    validation.arErrorText = localeStore.t('required_field_error');
  }

  if (!name.en?.trim()) {
    [validation.enValid, isValid] = [false, false];
    validation.enErrorText = localeStore.t('required_field_error');
  }

  return [validation, isValid];
};

const getEmptyTableObj = () => {
  return { id: Math.floor(randomFloatNumber()), isNew: true, name: { ar: '', en: '' } };
};

export default DineInSectionModal;
